<template>
  <div
    class="row col-12 bet py-3 mx-0 mb-1"
    :class="{
      win:
        (bet.outcome === 'winner' || bet.outcome === 'half-win') &&
        !bet.adminRefund,
      lose:
        (bet.outcome === 'loser' || bet.outcome === 'half-loss') &&
        !bet.adminRefund,
      refunded: bet.adminRefund || bet.outcome === 'refunded',
      betNormal:
        (bet.outcome !== 'winner' || bet.outcome !== 'loser') &&
        !bet.adminRefund,
    }"
  >
    <div class="col-12">
      <div class="row">
        <div
          class="col-2 d-none d-md-flex justify-content-center flex-nowrap align-items-center"
        >
          <div class="bet-time mediaHide">
            {{ dayjs(bet.game.start).format('hh:mm A') }}
            {{ dayjs(bet.game.start).format('DD MMM') }}
          </div>
        </div>
        <span
          class="col-md-6 col-lg-5 col-10 d-flex d-md-block justify-content-center flex-nowrap align-items-center bet-main-info"
        >
          <div v-if="bet.game.league && !bet.game.league.includes('PROPS')">
            {{ getSideString(bet.game, bet) }} {{ computedOdds }}
          </div>
          <div v-else>
            {{ getSideStringProps(bet.game, bet) }} {{ computedOdds }}
          </div>
          <informational-tooltip
            v-if="showTooltip && bet.game.participants.length"
            class="text-lowercase font-weight-light d-inline-flex"
            :text-content="`${bet.game.participants[0].mainPitcher}`"
          />
          <div
            v-if="bet.game.league && !bet.game.league.includes('PROPS')"
            class="bet-teams font-weight-normal"
          >
            {{
              userStore.user.defaultRotationNumbers
                ? bet.game.participants[0].rotationNumber
                : ''
            }}
            {{ bet.game.participants[0].longName }} @
            {{
              userStore.user.defaultRotationNumbers
                ? bet.game.participants[1].rotationNumber
                : ''
            }}
            {{ bet.game.participants[1].longName }}
            <half-result-badge :session="bet" />
          </div>
          <div v-else class="bet-teams font-weight-normal">
            {{ bet.game.participants[0].longName }} @
            {{ bet.game.participants[1].longName }}
            <half-result-badge :session="bet" />
          </div>
        </span>
        <!-- Mobile Risk/Win !-->
        <div
          class="d-md-none col-2 d-flex justify-content-center flex-nowrap align-items-center bet-risk"
          @click="$emit('displayBet', bet)"
        >
          <u> {{ noDecimal(computedRisk) }}/{{ noDecimal(computedWin) }} </u>
        </div>
        <div
          class="col-1 d-none d-md-flex justify-content-center flex-nowrap align-items-center bet-risk"
        >
          ${{ sessionBetsRound(computedRisk) }}
        </div>
        <div
          class="col-md-2 col-lg-1 d-none d-md-flex justify-content-center flex-nowrap align-items-center bet-risk"
        >
          ${{ sessionBetsRound(computedWin) }}
        </div>
        <div
          v-if="bet.origin === 'offer'"
          class="col-2 d-lg-flex d-none justify-content-center flex-nowrap align-items-center bet-type"
        >
          OFFER
        </div>
        <div
          v-else
          class="col-2 d-lg-flex d-none justify-content-center flex-nowrap align-items-center bet-type"
        >
          TAKEN
        </div>
        <div
          class="col-1 d-md-flex d-none justify-content-center flex-nowrap align-items-center bet-plus"
        >
          <font-awesome-icon
            :icon="faPlus"
            role="button"
            tabindex="0"
            @click="$emit('displayBet', bet)"
          />
        </div>
        <div
          class="d-flex d-md-none col-10 justify-content-center flex-nowrap align-items-center"
        >
          <div
            v-if="bet.game.participants.length"
            class="bet-small font-weight-normal"
          >
            {{ bet.game.participants[0].longName }} @
            {{ bet.game.participants[1].longName }}
            <half-result-badge :session="bet" />
          </div>
          <div v-else class="bet-small font-weight-normal">
            {{ bet.game.eventName }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { formatOdds, formatOddsNoCommission } from '@/core/oddsFormatUtils';
import { computed } from 'vue';
import dayjs from 'dayjs';
import {
  calculateWin,
  getSideString,
  noDecimal,
  getSideStringProps,
} from '@/core/utils';
import {
  commissionAdjustedRisk,
  commissionAdjustedWin,
} from '@/core/commissionUtils';
import { useUserStore } from '@/stores/user';
import InformationalTooltip from '@/components/global/tooltips/InformationalTooltip.vue';
import HalfResultBadge from '@/components/userBets/HalfResultBadge.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';

const userStore = useUserStore();
const props = defineProps({
  objSide: {
    type: Object,
    required: true,
  },
  bet: {
    type: Object,
    required: true,
  },
  league: {
    type: String,
    default: '',
  },
});

const computedRisk = computed(() => {
  if (props.bet.origin === 'offer') return props.bet.bet;
  return commissionAdjustedRisk(
    props.bet.bet,
    props.bet.odds,
    props.bet.game.league.toLowerCase(),
    props.bet.type,
    userStore,
  );
});
const computedWin = computed(() => {
  const regWin = calculateWin(props.bet.bet, props.bet.odds);
  if (props.bet.origin === 'offer') return regWin;
  return commissionAdjustedWin(
    regWin,
    props.bet.game.league.toLowerCase(),
    props.bet.type,
    userStore,
  );
});
const computedOdds = computed(() => {
  if (props.bet.origin === 'offer')
    return formatOddsNoCommission(props.bet.odds);
  return formatOdds(
    props.bet.odds,
    props.bet.game.league.toLowerCase(),
    props.bet.type,
    true,
    userStore,
  );
});

const showTooltip = computed(() => {
  const label = props.bet.game.participants[0]?.mainPitcher;
  if (!label) return false;
  return (
    props.bet.game.league.toLowerCase().includes('series') ||
    props.bet.game.sport === 'golf' ||
    props.bet.game.sport === 'politics'
  );
});

function sessionBetsRound(RiskOrWin) {
  if (RiskOrWin > 1000) {
    const valueBet = Math.round(Number.parseFloat(RiskOrWin));
    return valueBet;
  }
  const valueBet = parseFloat(Number(RiskOrWin).toFixed(2));

  if (Number.isInteger(RiskOrWin)) {
    return parseFloat(Number(RiskOrWin).toFixed(0));
  }
  return valueBet;
}

const emit = defineEmits(['displayBet']);
function displayBet() {
  //EventBus.$emit('displayBet', this.bet);
  emit('displayBet', props.bet);
}
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/tokens.scss' as main;

.container-positions {
  padding-left: 20px;
  padding-right: 20px;
}
.box-position {
  background: main.$white;
  margin: 0;
  margin-left: -20px;
  margin-right: -20px;
  margin-bottom: 16px;
}
.betNormal {
  background: main.$grey-500 !important;
}
.bet {
  border: 3px;
  border-top: none !important;
  border-radius: 4px;
  box-shadow: 0 2px 4px 0 main.$blue-1200;
  color: main.$blue-1100;
  font-size: small;
  margin: 0;
  width: 100%;
  padding-left: 5px !important;
  padding-right: 5px !important;

  &-main-info {
    text-transform: uppercase;
    font-weight: main.$font-weight-bold;
    color: main.$color-primary_01;
    font-size: main.$font-size-sm;
  }
  &-risk {
    font-size: main.$font-size-sm;
    color: main.$blue-1100;
    font-weight: main.$font-weight-bold;
  }
  &-type {
    font-size: main.$font-size-sm;
    color: main.$blue-1100;
    font-weight: main.$font-weight-bold;
  }
  &-plus {
    font-size: main.$font-size-sm;
    color: main.$blue-1100;
    cursor: pointer;
    font-weight: main.$font-weight-bold;
  }
  &-time {
    font-size: x-small;
    color: main.$black-203;
    font-weight: main.$font-weight-normal;
    text-transform: uppercase;
  }
  &-teams {
    color: main.$black-203;
    font-size: x-small;
    @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
      display: none;
    }
  }
  &-small {
    color: main.$black-203;
    font-size: x-small;
    text-transform: uppercase;
  }
  .win,
  .lose,
  .cancelled,
  .refunded {
    margin: 0;
    padding: 0.35rem 2rem;
    text-align: center;
    display: inline-block;
    font-family: inherit;
    border-radius: 4px;
    font-size: main.$font-size-xs;
    color: main.$white;
  }
  .lose {
    background: main.$red-101_01;
  }
  .cancelled,
  .refunded {
    background: main.$yellow-090;
  }
}

.short-info {
  font-size: small;
  font-weight: bold;
  color: main.$blue-702;
  text-align: center;
}
.win {
  background: main.$green-402 !important;
}
.lose {
  background: main.$red-101_01 !important;
}
.refunded {
  background: main.$yellow-090 !important;
}
</style>
